import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';

const STATUS_LABELS = {
  UPLOADED: 'Subido',
  VALIDATED: 'Validado',
  PROCESSED: 'Procesado',
  EXPORTING: 'Exportando',
  EXPORTED: 'Exportado',
  FAILED: 'Error',
};

const TYPE_LABELS = {
  contacts: 'Contactos',
  bank_accounts: 'Cuentas bancarias',
  invoices: 'Facturas',
  expenses: 'Gastos',
};

const HoldedExportsList = ({ title, description }) => {
  const { activeTenant } = useTenant();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadBatches = async () => {
    if (!activeTenant) return;
    try {
      setLoading(true);
      const res = await api.get(`/imports/batches?tenant_id=${activeTenant}`);
      setBatches(res.data || []);
    } catch (error) {
      console.error('Error loading holded exports:', error);
      toast.error('Error al cargar exportaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBatches();
  }, [activeTenant]);

  const formatDate = (value) => {
    if (!value) return '-';
    return value.split('T')[0];
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="holded-exports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-heading font-bold">{title}</h1>
          {description && <p className="text-muted-foreground">{description}</p>}
        </div>
        <Button variant="outline" onClick={loadBatches} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Actualizar
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Historial de batches</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-muted-foreground mb-4">Cargando batches...</p>}
          {!loading && batches.length === 0 && (
            <p className="text-sm text-muted-foreground">No hay exportaciones todavia.</p>
          )}
          {batches.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Archivo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Totales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => {
                  const totals = batch.totals || {};
                  return (
                    <TableRow key={batch.id}>
                      <TableCell>{formatDate(batch.created_at)}</TableCell>
                      <TableCell>{TYPE_LABELS[batch.type] || batch.type}</TableCell>
                      <TableCell className="truncate max-w-[220px]" title={batch.filename}>
                        {batch.filename}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex rounded-full bg-muted px-2 py-1 text-xs">
                          {STATUS_LABELS[batch.status] || batch.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {totals.rows_total ?? 0} total / {totals.rows_valid ?? 0} ok / {totals.rows_invalid ?? 0} err
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HoldedExportsList;
